import {
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import { useCallback, useTransition } from 'react';

import { generateNonce } from '@/utils/general';

import { searchParamsItems } from '../../search/search-results/search-params';

type NameFilter = {
  name: string;
  isChecked: boolean;
  children?: NameFilter[];
};

type IdFilter = {
  id: number;
  isChecked: boolean;
  children?: NameFilter[];
};

type IdSelectedFilter = {
  id: number;
  isSelected: boolean;
  children?: undefined;
};

function convertIsSelectedToChecked(
  inputFilter: (NameFilter | IdFilter | IdSelectedFilter)[],
) {
  return inputFilter.map((filter) => {
    if ('isSelected' in filter && !('isChecked' in filter)) {
      return {
        ...filter,
        isChecked: filter.isSelected,
      };
    }
    return { ...filter };
  });
}

export function isFilterEqual(
  inputFilterA: (NameFilter | IdFilter | IdSelectedFilter)[],
  inputFilterB: (NameFilter | IdFilter | IdSelectedFilter)[],
): boolean {
  const filterA = convertIsSelectedToChecked(inputFilterA);
  const filterB = convertIsSelectedToChecked(inputFilterB);

  if (filterA.length !== filterB.length) {
    return false;
  }

  const sortedA = [...filterA].sort((a, b) => {
    const nameA = 'name' in a ? a.name : a.id.toString();
    const nameB = 'name' in b ? b.name : b.id.toString();

    return nameA.localeCompare(nameB);
  });

  const sortedB = [...filterB].sort((a, b) => {
    const nameA = 'name' in a ? a.name : a.id.toString();
    const nameB = 'name' in b ? b.name : b.id.toString();

    return nameA.localeCompare(nameB);
  });

  return sortedA.every((filterItemA, index) => {
    const filterItemB = sortedB[index];

    if (!filterItemB) return false;

    const getCompareValue = (item: NameFilter | IdFilter | IdSelectedFilter) =>
      'id' in item ? item.id : item.name;

    const areBasicPropsEqual =
      getCompareValue(filterItemA) === getCompareValue(filterItemB) &&
      filterItemA.isChecked === filterItemB.isChecked;

    if (!areBasicPropsEqual) return false;

    if (filterItemA.children && filterItemB.children) {
      return isFilterEqual(filterItemA.children, filterItemB.children);
    }

    return !filterItemA.children && !filterItemB.children;
  });
}

export function useClearSearch() {
  const [_isPending, startTransition] = useTransition();
  const [_queryStates, setQueryStates] = useQueryStates(
    {
      search: parseAsString,
      page: parseAsInteger.withDefault(1),
    },
    {
      shallow: false,
      startTransition,
    },
  );

  return () => {
    setQueryStates({
      search: null,
      page: 1,
    });
  };
}

export function useClearFilters() {
  const keep = ['search', 'type'];
  const [_isPending, startTransition] = useTransition();

  const items = { ...searchParamsItems };

  const [_queryStates, setQueryStates] = useQueryStates(searchParamsItems, {
    shallow: false,
    startTransition,
  });

  return () => {
    const toClear: {
      [key in keyof typeof items]?: null;
    } = {};

    Object.keys(items).forEach(function (key) {
      if (!keep.includes(key)) {
        toClear[key as keyof typeof items] = null;
      }
    });

    setQueryStates(toClear);
  };
}

export function useResetPage() {
  const [_isPending, _startTransition] = useTransition();
  const [_queryPage, setQueryPage] = useQueryState('page', parseAsInteger.withDefault(1));

  return () => {
    setQueryPage(1);
  };
}

export function useQueryNonce() {
  const [nonce, setQueryNonce] = useQueryState('nonce');

  const generate = useCallback(() => {
    setQueryNonce(generateNonce());
  }, []);

  return {
    nonce,
    generate,
  };
}
